// SNIGJ - Enhanced Settings Page with Profile, Customization, Multi-currency
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCurrency } from '../context/CurrencyContext';
import { useAppearance } from '../context/AppearanceContext';
import { t, AVAILABLE_LANGUAGES } from '../lib/i18n';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { 
  User, Globe, Moon, Sun, Bell, Shield, Key, Save,
  Phone, Camera, Palette, Type, Wallet,
  Check, RefreshCw, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SettingsPage = () => {
  const { user, language, updateLanguage, refreshUser } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const { 
    preferredCurrency, 
    updatePreferredCurrency, 
    exchangeRates, 
    ratesLastUpdated,
    convertAmount,
    formatCurrency,
    currencies
  } = useCurrency();
  
  // Use the new AppearanceContext
  const { 
    settings: appearanceSettings, 
    updateSetting, 
    updateSettings,
    applyPreset, 
    resetToDefaults, 
    fonts, 
    colorPresets 
  } = useAppearance();
  
  const fileInputRef = useRef(null);
  
  // Profile state
  const [profile, setProfile] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone_primary: user?.phone_number || '',
    phone_secondary: user?.phone_secondary || '',
    phone_emergency: user?.phone_emergency || '',
    address: user?.address || '',
    picture: user?.picture || ''
  });
  
  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: false
  });
  
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // Update profile state when user changes
  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || '',
        phone_primary: user.phone_number || '',
        phone_secondary: user.phone_secondary || '',
        phone_emergency: user.phone_emergency || '',
        address: user.address || '',
        picture: user.picture || ''
      });
    }
  }, [user]);

  const handleProfilePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(language === 'fr' ? 'Image trop grande (max 5MB)' : 'Image too large (max 5MB)');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
        setProfile({ ...profile, picture: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/user/profile', {
        name: profile.name,
        phone_number: profile.phone_primary,
        phone_secondary: profile.phone_secondary,
        phone_emergency: profile.phone_emergency,
        address: profile.address,
        picture: profile.picture
      });
      toast.success(language === 'fr' ? 'Profil mis à jour' : 'Profile updated');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  // Handle font change - INSTANT
  const handleFontChange = (fontValue) => {
    updateSetting('font', fontValue);
    toast.success(language === 'fr' ? 'Police appliquée' : 'Font applied');
  };

  // Handle font size change - INSTANT
  const handleFontSizeChange = (value) => {
    updateSetting('fontSize', value[0]);
  };

  // Handle color change - INSTANT
  const handleColorChange = (colorType, value) => {
    updateSetting(colorType, value);
  };

  // Handle preset selection - INSTANT
  const handlePresetSelect = (preset) => {
    applyPreset(preset);
    toast.success(language === 'fr' ? `Thème "${preset.name}" appliqué` : `Theme "${preset.name}" applied`);
  };

  // Handle reset - INSTANT
  const handleReset = () => {
    resetToDefaults();
    toast.success(language === 'fr' ? 'Paramètres réinitialisés' : 'Settings reset');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {t(language, 'nav.settings')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {language === 'fr' ? 'Gérez votre profil et vos préférences' : 'Manage your profile and preferences'}
          </p>
        </div>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="profile" className="gap-2" data-testid="tab-profile">
              <User className="w-4 h-4" />
              {language === 'fr' ? 'Profil' : 'Profile'}
            </TabsTrigger>
            <TabsTrigger value="appearance" className="gap-2" data-testid="tab-appearance">
              <Palette className="w-4 h-4" />
              {language === 'fr' ? 'Apparence' : 'Appearance'}
            </TabsTrigger>
            <TabsTrigger value="currency" className="gap-2" data-testid="tab-currency">
              <Wallet className="w-4 h-4" />
              {language === 'fr' ? 'Devise' : 'Currency'}
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2" data-testid="tab-security">
              <Shield className="w-4 h-4" />
              {language === 'fr' ? 'Sécurité' : 'Security'}
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {language === 'fr' ? 'Informations personnelles' : 'Personal Information'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' ? 'Mettez à jour vos informations de contact' : 'Update your contact information'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Profile Photo */}
                <div className="flex items-center gap-6">
                  <div className="relative">
                    {previewImage || profile.picture ? (
                      <img 
                        src={previewImage || profile.picture} 
                        alt="" 
                        className="w-24 h-24 rounded-full object-cover border-4 border-primary"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-slate-900 font-bold text-3xl border-4 border-primary">
                        {profile.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <button 
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground hover:bg-primary/90"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                    <input 
                      ref={fileInputRef}
                      type="file" 
                      accept="image/*"
                      className="hidden"
                      onChange={handleProfilePhotoSelect}
                    />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{profile.name}</h3>
                    <p className="text-muted-foreground">{profile.email}</p>
                    <p className="text-sm mt-1">
                      <span className={`px-2 py-0.5 rounded text-xs classification-${user?.clearance_level?.toLowerCase()}`}>
                        {t(language, `roles.${user?.role}`)}
                      </span>
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Name and Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Nom complet' : 'Full Name'}</Label>
                    <Input 
                      value={profile.name}
                      onChange={(e) => setProfile({...profile, name: e.target.value})}
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input 
                      value={profile.email}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                </div>

                {/* Phone Numbers - 3 fields */}
                <div className="space-y-4">
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {language === 'fr' ? 'Numéros de téléphone' : 'Phone Numbers'}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        {language === 'fr' ? 'Téléphone principal' : 'Primary Phone'}
                      </Label>
                      <Input 
                        value={profile.phone_primary}
                        onChange={(e) => setProfile({...profile, phone_primary: e.target.value})}
                        placeholder="+243 XXX XXX XXX"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        {language === 'fr' ? 'Téléphone secondaire' : 'Secondary Phone'}
                      </Label>
                      <Input 
                        value={profile.phone_secondary}
                        onChange={(e) => setProfile({...profile, phone_secondary: e.target.value})}
                        placeholder="+243 XXX XXX XXX"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        {language === 'fr' ? "Téléphone d'urgence" : 'Emergency Phone'}
                      </Label>
                      <Input 
                        value={profile.phone_emergency}
                        onChange={(e) => setProfile({...profile, phone_emergency: e.target.value})}
                        placeholder="+243 XXX XXX XXX"
                      />
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Adresse' : 'Address'}</Label>
                  <Input 
                    value={profile.address}
                    onChange={(e) => setProfile({...profile, address: e.target.value})}
                    placeholder={language === 'fr' ? 'Votre adresse complète' : 'Your full address'}
                  />
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSaveProfile} disabled={saving} className="gap-2" data-testid="save-profile-btn">
                    <Save className="w-4 h-4" />
                    {saving ? (language === 'fr' ? 'Enregistrement...' : 'Saving...') : (language === 'fr' ? 'Enregistrer le profil' : 'Save Profile')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Appearance Tab - NOW WITH INSTANT APPLY */}
          <TabsContent value="appearance">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Theme & Language */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                    {language === 'fr' ? 'Thème & Langue' : 'Theme & Language'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">{language === 'fr' ? 'Mode sombre' : 'Dark Mode'}</Label>
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' ? 'Activer le thème sombre' : 'Enable dark theme'}
                      </p>
                    </div>
                    <Switch checked={isDark} onCheckedChange={toggleTheme} data-testid="dark-mode-toggle" />
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-3">
                    <Label className="font-medium flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      {language === 'fr' ? 'Langue' : 'Language'}
                    </Label>
                    <div className="grid grid-cols-3 gap-2">
                      {AVAILABLE_LANGUAGES?.map((lang) => (
                        <Button
                          key={lang.code}
                          variant={language === lang.code ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateLanguage(lang.code)}
                          className="text-xs"
                          data-testid={`lang-${lang.code}`}
                        >
                          {lang.native}
                        </Button>
                      )) || (
                        <>
                          <Button variant={language === 'fr' ? 'default' : 'outline'} size="sm" onClick={() => updateLanguage('fr')}>Français</Button>
                          <Button variant={language === 'en' ? 'default' : 'outline'} size="sm" onClick={() => updateLanguage('en')}>English</Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Typography - INSTANT APPLY */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Type className="w-5 h-5" />
                    {language === 'fr' ? 'Typographie' : 'Typography'}
                  </CardTitle>
                  <CardDescription>
                    {language === 'fr' ? 'Les changements s\'appliquent instantanément' : 'Changes apply instantly'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <Label>{language === 'fr' ? "Police d'écriture" : 'Font Family'}</Label>
                    <Select 
                      value={appearanceSettings.font} 
                      onValueChange={handleFontChange}
                    >
                      <SelectTrigger data-testid="font-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {fonts.map((font) => (
                          <SelectItem 
                            key={font.value} 
                            value={font.value} 
                            style={{ fontFamily: font.family }}
                          >
                            {font.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <Label>{language === 'fr' ? 'Taille du texte' : 'Font Size'}</Label>
                      <span className="text-sm text-muted-foreground">{appearanceSettings.fontSize}px</span>
                    </div>
                    <Slider 
                      value={[appearanceSettings.fontSize]}
                      onValueChange={handleFontSizeChange}
                      min={12}
                      max={24}
                      step={1}
                      data-testid="font-size-slider"
                    />
                    <p className="text-muted-foreground" style={{ fontSize: `${appearanceSettings.fontSize}px` }}>
                      {language === 'fr' ? 'Aperçu de la taille du texte' : 'Text size preview'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Colors - INSTANT APPLY */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Palette className="w-5 h-5" />
                      {language === 'fr' ? 'Couleurs personnalisées' : 'Custom Colors'}
                    </span>
                    <Button variant="outline" size="sm" onClick={handleReset} className="gap-2" data-testid="reset-appearance-btn">
                      <RefreshCw className="w-4 h-4" />
                      {language === 'fr' ? 'Réinitialiser' : 'Reset'}
                    </Button>
                  </CardTitle>
                  <CardDescription>
                    {language === 'fr' ? 'Cliquez sur un thème pour l\'appliquer instantanément' : 'Click a theme to apply instantly'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Presets */}
                  <div className="space-y-3">
                    <Label>{language === 'fr' ? 'Thèmes prédéfinis' : 'Color Presets'}</Label>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                      {colorPresets.map((preset) => (
                        <button
                          key={preset.name}
                          onClick={() => handlePresetSelect(preset)}
                          className={`p-3 rounded border-2 transition-all hover:border-primary ${
                            appearanceSettings.primaryColor === preset.primary &&
                            appearanceSettings.secondaryColor === preset.secondary &&
                            appearanceSettings.accentColor === preset.accent
                              ? 'border-primary bg-primary/10'
                              : 'border-border'
                          }`}
                          data-testid={`preset-${preset.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                        >
                          <div className="flex justify-center gap-1 mb-2">
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.secondary }} />
                            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }} />
                          </div>
                          <span className="text-xs">{preset.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Custom Colors - INSTANT */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-3">
                      <Label>{language === 'fr' ? 'Couleur principale' : 'Primary Color'}</Label>
                      <div className="flex gap-3">
                        <input 
                          type="color" 
                          value={appearanceSettings.primaryColor}
                          onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                          className="w-14 h-10 rounded cursor-pointer border"
                          data-testid="primary-color-input"
                        />
                        <Input 
                          value={appearanceSettings.primaryColor}
                          onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label>{language === 'fr' ? 'Couleur secondaire' : 'Secondary Color'}</Label>
                      <div className="flex gap-3">
                        <input 
                          type="color" 
                          value={appearanceSettings.secondaryColor}
                          onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                          className="w-14 h-10 rounded cursor-pointer border"
                          data-testid="secondary-color-input"
                        />
                        <Input 
                          value={appearanceSettings.secondaryColor}
                          onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <Label>{language === 'fr' ? "Couleur d'accent" : 'Accent Color'}</Label>
                      <div className="flex gap-3">
                        <input 
                          type="color" 
                          value={appearanceSettings.accentColor}
                          onChange={(e) => handleColorChange('accentColor', e.target.value)}
                          className="w-14 h-10 rounded cursor-pointer border"
                          data-testid="accent-color-input"
                        />
                        <Input 
                          value={appearanceSettings.accentColor}
                          onChange={(e) => handleColorChange('accentColor', e.target.value)}
                          className="flex-1 font-mono"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview Box */}
                  <div 
                    className="p-4 rounded-lg border-2"
                    style={{ 
                      borderColor: appearanceSettings.accentColor,
                      background: `linear-gradient(135deg, ${appearanceSettings.primaryColor}20, ${appearanceSettings.secondaryColor}20)`
                    }}
                  >
                    <p className="font-medium" style={{ color: appearanceSettings.primaryColor }}>
                      {language === 'fr' ? 'Aperçu des couleurs' : 'Color Preview'}
                    </p>
                    <p className="text-sm" style={{ color: appearanceSettings.secondaryColor }}>
                      {language === 'fr' ? 'Voici comment vos couleurs personnalisées apparaîtront' : 'This is how your custom colors will appear'}
                    </p>
                    <button 
                      className="mt-2 px-4 py-2 rounded text-white text-sm font-medium"
                      style={{ backgroundColor: appearanceSettings.accentColor }}
                    >
                      {language === 'fr' ? 'Bouton exemple' : 'Example Button'}
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Currency Tab */}
          <TabsContent value="currency">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  {language === 'fr' ? 'Devise & Paiements' : 'Currency & Payments'}
                </CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? "Choisissez votre devise préférée pour l'affichage des montants" 
                    : 'Choose your preferred currency for displaying amounts'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Last update info */}
                {ratesLastUpdated && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                    <Clock className="w-4 h-4" />
                    {language === 'fr' ? 'Taux mis à jour le:' : 'Rates updated:'}{' '}
                    {new Date(ratesLastUpdated).toLocaleString(language === 'fr' ? 'fr-FR' : 'en-US')}
                  </div>
                )}

                <div className="space-y-3">
                  <Label className="text-base font-medium">
                    {language === 'fr' ? "Devise d'affichage" : 'Display Currency'}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {currencies.map((currency) => (
                      <button
                        key={currency.code}
                        onClick={() => updatePreferredCurrency(currency.code)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          preferredCurrency === currency.code 
                            ? 'border-primary bg-primary/10' 
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`currency-${currency.code}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-2xl font-bold">{currency.symbol}</span>
                          {preferredCurrency === currency.code && (
                            <Check className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <p className="font-medium text-left">{currency.code}</p>
                        <p className="text-sm text-muted-foreground text-left">
                          {language === 'fr' ? currency.name : currency.nameEn}
                        </p>
                        <p className="text-xs text-muted-foreground text-left mt-1">
                          1 USD = {exchangeRates[currency.code] || 1} {currency.symbol}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Conversion Preview with Dynamic Rates */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">
                    {language === 'fr' ? 'Aperçu des conversions' : 'Conversion Preview'}
                  </Label>
                  <div className="grid grid-cols-3 gap-4">
                    {currencies.map((currency) => (
                      <div key={currency.code} className={`p-4 rounded text-center ${
                        preferredCurrency === currency.code ? 'bg-primary/10 border-2 border-primary' : 'bg-muted'
                      }`}>
                        <p className="text-sm text-muted-foreground">100 USD =</p>
                        <p className="text-xl font-bold">
                          {formatCurrency(convertAmount(100, 'USD', currency.code), currency.code)}
                        </p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {language === 'fr' 
                      ? "* Taux de conversion définis par l'administration" 
                      : '* Exchange rates set by administration'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    {language === 'fr' ? 'Mot de passe' : 'Password'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}</Label>
                      <Input type="password" placeholder="••••••••" />
                    </div>
                    <div className="space-y-2">
                      <Label>{language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}</Label>
                      <Input type="password" placeholder="••••••••" />
                    </div>
                  </div>
                  <Button variant="outline">
                    {language === 'fr' ? 'Changer le mot de passe' : 'Change Password'}
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Notifications
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">{language === 'fr' ? 'Notifications par email' : 'Email Notifications'}</Label>
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' ? 'Recevoir les notifications par email' : 'Receive notifications by email'}
                      </p>
                    </div>
                    <Switch 
                      checked={notifications.emailNotifications}
                      onCheckedChange={(v) => setNotifications({...notifications, emailNotifications: v})}
                    />
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="font-medium">{language === 'fr' ? 'Notifications SMS' : 'SMS Notifications'}</Label>
                      <p className="text-sm text-muted-foreground">
                        {language === 'fr' ? 'Recevoir les notifications par SMS' : 'Receive notifications by SMS'}
                      </p>
                    </div>
                    <Switch 
                      checked={notifications.smsNotifications}
                      onCheckedChange={(v) => setNotifications({...notifications, smsNotifications: v})}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
